import { getDefaultVideoEncoder } from '../constants/encoders';
import { getEnabledSources } from '../routes/_authenticated.canvases/$canvasId/-sceneDropdownMenu/getEnabledSources';
import {
  ReadScopedStorageItemInfoResult,
  SceneInfo,
  SceneItemInfo,
  VideoCompositionId,
  VideoCompositionInfo,
} from '../types/obs';
import { AnyString } from '../types/util';
import { ensureOBSPresence } from '../utils/ensureOBSPresence';
import { promisefy } from '../utils/promisefy';
import { ScopedStorage } from './ScopedStorageService';
import { v4 as uuidv4 } from 'uuid';

/**
 * Gotta create a union type here so that we get the named types when hovering over the methods
 * Otherwise we'd just get `string` for both `TriggerScene` and `TriggeredScene` and it's unclear which is which.
 */
type TriggerScene = string | AnyString;
type TriggeredScene = string | AnyString;

export type VideoCompositionWithScenes = VideoCompositionInfo & {
  scenes: (SceneInfo & {
    sources: SceneItemInfo[];
  })[];
};

export type TransitionSettings = {
  isSameAsNativeCanvas: boolean;
};

export class CompositionsService {
  constructor() {
    void 0;
  }

  static getScopedStorageIdObject(id: string) {
    return {
      container: 'compositions',
      scope: 'streamelements_multi-streaming',
      item: id,
    };
  }

  static getScopedStorageSceneLinkingIdObject() {
    return {
      container: 'compositions',
      scope: 'streamelements_multi-streaming',
      item: 'scene-links',
    };
  }

  static getScopedStorageTransitionSettingsIdObject(id: string) {
    return {
      container: 'transitions',
      scope: 'streamelements_multi-streaming',
      item: id,
    };
  }

  static async getCompositionsFromStorage() {
    await ensureOBSPresence();
    const allItems = await ScopedStorage.getAllScopedStorageJsonItems({
      scope: 'streamelements_multi-streaming',
      container: 'compositions',
    });

    const compositions = await Promise.allSettled(
      allItems.filter(({ item }) => !['scene-links'].includes(item)).map(({ item }) => this.getCompositionFromStorageById(item))
    );

    return Object.fromEntries(
      compositions.filter((c) => c.status === 'fulfilled').map((c) => [c?.value?.item, c?.value?.content]).filter((c) => c[1])
    );
  }

  static async getCompositionFromStorageById(id: string) {
    await ensureOBSPresence();
    return await ScopedStorage.readScopedStorageJsonItem<VideoCompositionWithScenes>({
      container: 'compositions',
      scope: 'streamelements_multi-streaming',
      item: id,
    });
  }

  static async storeComposition(
    input: string | VideoCompositionWithScenes
  ): Promise<ReadScopedStorageItemInfoResult<VideoCompositionWithScenes>> {
    await ensureOBSPresence();

    const composition = typeof input === 'string' ? await this.getCompositionFromMemory(input) : input;

    if (!composition) {
      throw new Error('Composition not found');
    }

    return ScopedStorage.writeScopedStorageJsonItem({
      ...CompositionsService.getScopedStorageIdObject(composition.id),
      content: composition,
    });
  }

  static async storeTransitionSettings(videoCompositionid: string, input: TransitionSettings): Promise<void> {
    await ensureOBSPresence();

    await ScopedStorage.writeScopedStorageJsonItem({
      ...CompositionsService.getScopedStorageTransitionSettingsIdObject(videoCompositionid),
      content: input,
    });
  }

  static async getTransitionSettings(videoCompositionid: string): Promise<TransitionSettings> {
    await ensureOBSPresence();

    const data = await ScopedStorage.readScopedStorageJsonItem({
      ...CompositionsService.getScopedStorageTransitionSettingsIdObject(videoCompositionid),
    });

    if (data?.content) {
      return data.content as TransitionSettings;
    } else {
      return {
        isSameAsNativeCanvas: false,
      };
    }
  }

  static async registerComposition(input: VideoCompositionWithScenes) {
    const { scenes, id: videoCompositionId, streamingVideoEncoders, ...composition } = input;

    return await navigator.locks.request('CompositionService.registerComposition.createScenes', async () => {
      console.log('--- LOCKED: CompositionService.registerComposition.createScenes');

      console.info('Registering composition', input);

      const currentVideoCompositions = await promisefy(window.host.getAllVideoCompositions)();

      const registeredComposition =
        currentVideoCompositions[videoCompositionId] ||
        (await promisefy(window.host.addVideoComposition)({
          ...composition,
          id: videoCompositionId,
          streamingVideoEncoders: streamingVideoEncoders.map((encoder) => {
            if (!encoder.settings) {
              console.warn('No "settings" property found on streaming video encoder, using empty object because otherwise SE.Live will reject the registration.');
              encoder.settings = {};
            }
            return encoder;
          }),
        }));

      if (!registeredComposition) {
        throw new Error('Failed to create composition');
      }

      console.info('Composition registered', registeredComposition);

      console.info('Registering scenes', scenes);

      const registeredScenes: (SceneInfo & { sources: SceneItemInfo[] })[] = [];

      const allExistingScenes = await promisefy(window.host.getAllScenes)({ videoCompositionId: registeredComposition.id });

      console.info('Fetched existing scenes: ', allExistingScenes);

      // First lets remove all scenes from the composition, leaving only the default scene
      const defaultScene = await promisefy(window.host.addScene)({
        videoCompositionId,
        name: 'Scene',
        auxiliaryData: {},
        //active: true,
      });

      console.info('Created default scene', defaultScene);

      await promisefy(window.host.removeScenesByIds)(allExistingScenes.map(scene => scene.id));

      console.info('Removed existing scenes');

      for (const scene of scenes) {
        const { sources, ...sceneInfo } = scene;
        console.info('Registering scene', sceneInfo);

        const isFirstScene = !registeredScenes.length;

        console.info('Using default created scene id', isFirstScene);

        let registeredScene: SceneInfo;

        /**
         * When creating a composition, obs will create a default scene.
         * And if we have a scene with the same default name, obs will add a number suffix to it.
         * So we use the default scene but update its properties to match the first stored scene.
         */
        if (isFirstScene) {
          registeredScene = defaultScene;
          await promisefy(window.host.setScenePropertiesById)({
            ...sceneInfo,
            id: registeredScene.id,
            videoCompositionId: registeredComposition.id,
          });
        } else {
          registeredScene = await promisefy(window.host.addScene)({
            ...sceneInfo,
            videoCompositionId: registeredComposition.id,
          });

          console.info('Created scene', registeredScene);
        }

        if (!registeredScene?.id) {
          throw new Error('Failed to create scene');
        }

        console.info('Registering sources to scene', registeredScene);

        const registeredSources: SceneItemInfo[] = [];

        for (const source of sources) {
          console.info('Registering source', source);

          const currentSceneItemsMap = new Map(
            (await promisefy(window.host.getAllExistingVideoInputSources)()).map((source) => [source.name, source])
          );
      
          const existingSource = currentSceneItemsMap.get(source.name);

          if (existingSource) {
            console.info('A source with the name already exists, indicating that a source is being reused');
          }

          const enabledSources = await getEnabledSources();

          const registeredSource = await enabledSources[source.class]?.callback?.({
            ...source,
            compositionId: registeredComposition.id,
            sceneId: registeredScene.id,
            existingSourceId: existingSource?.id,
          });

          if (!registeredSource) {
            console.warn('Failed to create source', source);
            continue;
          }

          console.info('Setting source properties', {
            videoCompositionId: registeredComposition.id,
            sceneId: registeredScene.id,
          });

          registeredSource.videoCompositionId = registeredComposition.id;
          registeredSource.sceneId = registeredScene.id;
          registeredSources.push(registeredSource);
        }

        console.info('Registered sources', registeredSources);

        Object.assign(sceneInfo, {
          id: registeredScene.id,
          videoCompositionId: registeredComposition.id,
          sources: registeredSources,
        });

        registeredScenes.push(sceneInfo as SceneInfo & { sources: SceneItemInfo[] });
      }

      if (registeredScenes.length) {
        const newActiveScene = registeredScenes[0];

        await promisefy(window.host.setCurrentSceneById)(newActiveScene.id);
      }

      return {
        ...registeredComposition,
        scenes: registeredScenes,
      };
    }) as VideoCompositionWithScenes;
  }

  static async createVideoComposition(data: {
    videoCompositionName: string;
    videoFrame: { width: number; height: number };
  }) {
    const defaultVideoEncoder = await getDefaultVideoEncoder();

    const videoComposition = {
      id: uuidv4(),
      name: data.videoCompositionName,
      videoFrame: data.videoFrame,
      streamingVideoEncoders: [defaultVideoEncoder],
    } as VideoCompositionInfo;

    console.log('Trying to add video composition', videoComposition);

    const result = (await promisefy(window.host.addVideoComposition)(videoComposition)) as VideoCompositionWithScenes;

    if (!result) {
      throw new Error('Failed to create video composition');
    }

    const scenes = await promisefy(window.host.getAllScenes)({ videoCompositionId: result.id });
    result.scenes = scenes.map((scene) => ({
      ...scene,
      sources: [],
    }));

    await CompositionsService.storeComposition(result);

    return result;
  }

  static async removeCompositionStorage(id: string) {
    await ensureOBSPresence();
    await Promise.all([
      ScopedStorage.removeScopedStorageJsonItem({
        ...CompositionsService.getScopedStorageIdObject(id),
      }),
      ScopedStorage.removeScopedStorageJsonItem({
        ...CompositionsService.getScopedStorageTransitionSettingsIdObject(id),
      }),
    ]);
  }

  static async getActiveCompositions<R extends Record<VideoCompositionId, VideoCompositionWithScenes>>(): Promise<R> {
    await ensureOBSPresence();
    const storage = await this.getCompositionsFromStorage();
    const memory = (await promisefy(window.host.getAllVideoCompositions)()) as Record<
      VideoCompositionId,
      VideoCompositionWithScenes
    >;

    for (const compositionId in memory) {
      const scenes = (await promisefy(window.host.getAllScenes)({
        videoCompositionId: compositionId,
      }) || []) as (SceneInfo & { sources: SceneItemInfo[] })[];

      memory[compositionId].scenes = [];

      for (const scene of scenes) {
        scene.sources = await promisefy(window.host.getAllSceneItems)({
          id: scene.id,
          videoCompositionId: compositionId,
        });
        memory[compositionId].scenes.push(scene);
      }
    }

    return {
      ...storage,
      ...memory,
    } as R;
  }

  static async getCompositionFromMemory(id: VideoCompositionId) {
    await ensureOBSPresence();
    const result = await promisefy(window.host.getAllVideoCompositions)();
    const composition = result[id];

    if (!composition) {
      return null;
    }

    const scenes = await Promise.all(
      (
        await promisefy(window.host.getAllScenes)({
          videoCompositionId: composition.id,
        }) || []
      ).map(async (scene) => {
        const sources = await promisefy(window.host.getAllSceneItems)({
          id: scene.id,
          videoCompositionId: composition.id,
        });

        return {
          ...scene,
          sources: (sources || []).map((s) => ({
            ...s,
            videoCompositionId: composition.id,
            sceneId: scene.id,
          })),
        };
      })
    );

    return {
      ...composition,
      scenes,
    };
  }

  static async isCompositionInMemory(id: VideoCompositionId) {
    return !!this.getCompositionFromMemory(id);
  }

  private static serializeLinkedScenes(storage: Map<string, Set<string>>) {
    return Object.fromEntries(Array.from(storage.entries()).map(([key, value]) => [key, Array.from(value)]));
  }

  private static deserializeLinkedScenes(storage: { [k: string]: string[] }) {
    return new Map<TriggerScene, Set<TriggeredScene>>(
      Object.entries(storage).map(([key, value]) => [key, new Set(value)])
    );
  }

  static async getLinkedScenesStorage(): Promise<Map<TriggerScene, Set<TriggeredScene>>> {
    const items = await ScopedStorage.readScopedStorageJsonItem<{
      [k: string]: string[];
    }>({
      ...CompositionsService.getScopedStorageSceneLinkingIdObject(),
    });

    if (!items) {
      return new Map<TriggerScene, Set<TriggeredScene>>();
    }

    return this.deserializeLinkedScenes(items.content);
  }

  static async linkScenes(params: { triggerSceneName: string; targetSceneName: string; isBidirectional?: boolean }) {
    const { triggerSceneName, targetSceneName, isBidirectional = false } = params;

    const storage = await this.getLinkedScenesStorage().catch(() => new Map<string, Set<string>>());

    const triggerSceneStorage = storage.get(triggerSceneName) ?? new Set<string>();
    triggerSceneStorage.add(targetSceneName);
    storage.set(triggerSceneName, triggerSceneStorage);

    if (isBidirectional) {
      const targetSceneStorage = storage.get(targetSceneName) ?? new Set<string>();
      targetSceneStorage.add(triggerSceneName);
      storage.set(targetSceneName, targetSceneStorage);
    }

    const result = await ScopedStorage.writeScopedStorageJsonItem({
      ...CompositionsService.getScopedStorageSceneLinkingIdObject(),
      content: this.serializeLinkedScenes(storage),
    });

    console.info('Linking scenes', result);
  }

  static async unlinkScenes(props: { triggerSceneName: string; targetSceneName: string }) {
    const storage = await this.getLinkedScenesStorage();

    const storageA = storage.get(props.triggerSceneName) ?? new Set<string>();
    const storageB = storage.get(props.targetSceneName) ?? new Set<string>();

    storageA.delete(props.targetSceneName);
    storageB.delete(props.triggerSceneName);

    if (storageA.size === 0) {
      storage.delete(props.triggerSceneName);
    }

    if (storageB.size === 0) {
      storage.delete(props.targetSceneName);
    }

    await ScopedStorage.writeScopedStorageJsonItem({
      ...CompositionsService.getScopedStorageSceneLinkingIdObject(),
      content: this.serializeLinkedScenes(storage),
    });
  }

  static async renameSceneLink(params: { oldSceneName: string; newSceneName: string }) {
    const storage = await this.getLinkedScenesStorage();

    for (const [key, value] of storage.entries()) {
      if (key === params.oldSceneName) {
        storage.set(params.newSceneName, value);
        storage.delete(params.oldSceneName);
        continue;
      }

      if (value.has(params.oldSceneName)) {
        value.add(params.newSceneName);
        value.delete(params.oldSceneName);
      }
    }

    return ScopedStorage.writeScopedStorageJsonItem({
      ...CompositionsService.getScopedStorageSceneLinkingIdObject(),
      content: this.serializeLinkedScenes(storage),
    });
  }
}

export class CompositionMetadataService {}
